import { useNode } from "@craftjs/core";
import React, { useContext, useMemo } from "react";
import { PostFeaturedImageSettings } from "./settings";
import GrigoraCSSRenderer from "./../css-render";
import icon from "./icon";
import { GrigoraDashboardContext } from "../../dashboard/context";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";
import { GrigoraPreviewContext } from "../../post-preview/context";

var IMG_SIZES = [1920, 1600, 1080, 800, 500, 300];

export const PostFeaturedImage = (props) => {
  const { projectID, domain, cmsPosts } =
    useContext(GrigoraDashboardContext) ?? {};

  const previewContext = useContext(GrigoraPreviewContext);
  const {
    id,
    connectors: { connect, drag },
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));

  var {
    src = "https://cdn.grigora.co/defaults/img/post-featured-image-placeholder.webp",
    srcMode = "link",
    lazyLoad = true,
    altText,
    titleText,
    linkPost,
    openInNewTab,
    link = "#",
  } = props;

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames(
    {
      "g-post-featured-image": true,
      [`post-featured-image-${id}`]: id,
    },
    blockProps.className,
  );

  if (previewContext?.postData?.featured_image) {
    src = previewContext?.postData?.featured_image?.src;
    srcMode = previewContext?.postData?.featured_image?.srcMode;
  } else if (cmsPosts?.length > 0) {
    src = cmsPosts?.[0]?.featured_image?.src;
    srcMode = cmsPosts?.[0]?.featured_image?.srcMode;
  }

  const imgsrc = useMemo(() => {
    if (srcMode === "cdn") {
      if (src?.og) {
        return `https://cdn.grigora.co/projects/${
          src?.projectID ? src.projectID : projectID
        }/${src?.og}`;
      }
      return `https://cdn.grigora.co/projects/${
        src?.projectID ? src.projectID : projectID
      }/${src?.name}.${src?.extension}`;
    } else if (srcMode === "link") {
      return src;
    } /* only in editor */ else if (srcMode === "preview") {
      return src;
    }
    return undefined;
  }, [srcMode, src]);

  const srcSet = useMemo(() => {
    if (src && src.width) {
      let available_sizes = IMG_SIZES.filter((value) => value <= src.width);
      let available_size_urls = [];
      if (src?.og) {
        return `https://cdn.grigora.co/projects/${
          src?.projectID ? src.projectID : projectID
        }/${src?.og} ${src.width}w`;
      } else {
        available_size_urls.push(
          `https://cdn.grigora.co/projects/${
            src?.projectID ? src.projectID : projectID
          }/${src?.name}.${src?.extension} ${src.width}w`,
        );
      }
      available_sizes.map((size) => {
        if (src["resize" + size]) {
          available_size_urls.push(
            `https://cdn.grigora.co/projects/${
              src?.projectID ? src.projectID : projectID
            }/${src["resize" + size]} ${size}w`,
          );
        }
      });
      return available_size_urls.join(", ");
    }
    return undefined;
  }, [srcMode, src]);

  const sizes = useMemo(() => {
    if (src && src.width) {
      return `(max-width: ${src.width}px) 100vw,  ${src.width}px`;
    }
    return undefined;
  }, [srcMode, src]);

  return (
    <div
      {...blockProps}
      ref={(ref) => connect(drag(ref))}
      className={wrapperClass}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <style>{`${GrigoraCSSRenderer.blockCSS__PostFeaturedImage(
        id,
        props,
      )}`}</style>
      <>
        {linkPost ? (
          <a
            target={`${openInNewTab == true ? "_blank" : "_self"}`}
            onClick={(e) => e.preventDefault()}
            href={`https://${domain}/${cmsPosts?.[0]?.slug}`}
          >
            <img
              loading={lazyLoad ? "lazy" : "eager"}
              decoding={"async"}
              src={imgsrc}
              srcSet={srcSet}
              sizes={sizes}
              title={titleText}
              alt={`${
                altText ? altText : "No alt attribute set on the image."
              }`}
            />
          </a>
        ) : (
          <img
            loading={lazyLoad ? "lazy" : "eager"}
            decoding={"async"}
            src={imgsrc}
            srcSet={srcSet}
            sizes={sizes}
            title={titleText}
            alt={`${altText ? altText : "No alt attribute set on the image."}`}
          />
        )}
      </>
    </div>
  );
};

// To Avoid Mangling in Production
PostFeaturedImage.displayName = "PostFeaturedImage";

PostFeaturedImage.craft = {
  name: "Post Featured Image",
  props: {},
  related: {
    settings: PostFeaturedImageSettings,
    icon: icon,
    supports: [
      "grigora/entrance-animations",
      "grigora/css-transform-animations",
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/position",
      "grigora/sticky",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    category: "cms",
  },
  custom: {
    name: "Post Featured Image",
    slug: "grigora/post-featured-image",
  },
};
